import { FILE_STATUS, MAX_FILENAME_LENGTH } from "./fileManagerConstants";

/**
 * Format date in user timezone
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString) => {
  if (!dateString) return "-";

  try {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (error) {
    console.error("Error formatting date:", error);
    return "-";
  }
};

/**
 * Truncate filename with ellipsis
 * @param {string} filename - Full filename
 * @param {number} maxLength - Maximum length before truncation
 * @returns {string} Truncated filename
 */
export const truncateFilename = (filename, maxLength = MAX_FILENAME_LENGTH) => {
  if (!filename || filename.length <= maxLength) return filename;

  const lastDotIndex = filename.lastIndexOf(".");

  if (lastDotIndex === -1) {
    return `${filename.substring(0, maxLength - 3)}...`;
  }

  const extension = filename.substring(lastDotIndex);
  const nameWithoutExt = filename.substring(0, lastDotIndex);
  const truncatedName = nameWithoutExt.substring(
    0,
    maxLength - extension.length - 3
  );

  return `${truncatedName}...${extension}`;
};

/**
 * Get current user from token storage
 * @returns {string|null} User GPID or null
 */
export const getCurrentUser = () => {
  try {
    const tokenStorage = localStorage.getItem("okta-token-storage");
    if (!tokenStorage) return null;

    const parsed = JSON.parse(tokenStorage);
    return parsed?.accessToken?.claims?.gpid || null;
  } catch (error) {
    console.error("Error getting current user:", error);
    return null;
  }
};

/**
 * Get access token from storage
 * @returns {string|null} Access token or null
 */
export const getAccessToken = () => {
  try {
    const tokenStorage = localStorage.getItem("okta-token-storage");
    if (!tokenStorage) return localStorage.getItem("accessToken");

    const parsed = JSON.parse(tokenStorage);
    return parsed?.accessToken?.accessToken || localStorage.getItem("accessToken");
  } catch (error) {
    console.error("Error getting access token:", error);
    return localStorage.getItem("accessToken");
  }
};

/**
 * Check if files array has any processing files
 * @param {Array} files - Array of file objects
 * @returns {boolean} True if any file is processing
 */
export const hasProcessingFiles = (files) => {
  if (!Array.isArray(files)) return false;
  return files.some((file) => file.status === FILE_STATUS.PROCESSING);
};

/**
 * Filter files by operation type
 * @param {Array} files - Array of file objects
 * @param {string} operationType - EXPORT, IMPORT, or ALL
 * @returns {Array} Filtered files
 */
export const filterFilesByOperationType = (files, operationType) => {
  if (!Array.isArray(files)) return [];
  if (operationType === "ALL") return files;

  return files.filter((file) => file.operationType === operationType);
};

/**
 * Get filter preferences from localStorage
 * @param {string} context - Context type
 * @returns {Object} Filter preferences
 */
export const getFilterPreferences = (context) => {
  try {
    const key = `fileManager_filters_${context}`;
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error("Error getting filter preferences:", error);
    return {};
  }
};

/**
 * Save filter preferences to localStorage
 * @param {string} context - Context type
 * @param {Object} filters - Filter object
 */
export const saveFilterPreferences = (context, filters) => {
  try {
    const key = `fileManager_filters_${context}`;
    localStorage.setItem(key, JSON.stringify(filters));
  } catch (error) {
    console.error("Error saving filter preferences:", error);
  }
};

/**
 * Format file size in human readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return "-";

  const units = ["B", "KB", "MB", "GB"];
  let size = bytes;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }

  return `${size.toFixed(2)} ${units[unitIndex]}`;
};
